import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, unref as _unref } from "vue"

const _hoisted_1 = { class: "page-header page-header-with-tabs w-full" }
const _hoisted_2 = { class: "flex justify-content-between align-items-center w-full h-2rem" }
const _hoisted_3 = ["textContent"]
const _hoisted_4 = {
  key: 0,
  class: "page-header-tab-bar"
}
const _hoisted_5 = {
  key: 1,
  class: "page-header-tab-bar-right"
}
const _hoisted_6 = { key: 1 }

import { computed, onMounted, onBeforeMount, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';
import AppTabMenu from '@/components/AppTabMenu';
import ArchiveModal from '@/components/Modal/ArchiveModal/index';
import { ImpersonateButton, ResetPasswordButton, ActivateUserButton } from '@/components/Buttons';
import { UserHeaderTotals } from '.';
import { PersonStores, useArchiveStore, useAuthStore } from '@/store';
import { useRouting, can, useTenant } from '@/composables';
import { PERMISSIONS, TENANT_SETTINGS } from '@/enum';
import { UserList, UsersToBeArchived } from '..';


export default /*@__PURE__*/_defineComponent({
  __name: 'UserHeader',
  setup(__props) {

const toast = useToast();

const $routing = useRouting();
const $tenant = useTenant();

const profileStore = PersonStores.useUserProfileStore();
const archiveStore = useArchiveStore();
const authStore = useAuthStore();
const route = useRoute();

const userRoutes = ref([
  { route: '/users', label: 'All Students', component: UserList, visible: true },
  {
    route: '/users/to-be-archived',
    label: 'SIS To Be Archived',
    component: UsersToBeArchived,
    visible:
      can([PERMISSIONS.PEOPLE_TO_BE_ARCHIVED]) &&
      $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_PEOPLE_TO_BE_ARCHIVED),
  },
]);

const activeIndex = computed(() => ($routing.route.name === 'user.list' ? 0 : 1));

onBeforeMount(async () => {
  if (route.params.id && !profileStore.id) await profileStore.setUser(Number(route.params.id));

  profileStore.currentRoute = $routing.route.name;
});

/**
 * The default header title text.
 */
const headerTitleDefault = computed(() => ($routing.route.name === 'user.referrals' ? 'Students' : 'Users'));

/**
 * The header title reference value.
 */
const headerTitle = ref(headerTitleDefault.value);

/**
 * Whether or not the display the skeleton loader.
 */
const showLoader = ref(false);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => defineHeaderTitle());

/**
 * Watch for a change within the route in order to change the header title.
 */
$routing.routeChangeWatcher(() => {
  profileStore.currentRoute = $routing.route.name;
  defineHeaderTitle();
});

/**
 * Watch for a change within the user profile store in order to change the header title.
 */
watch(profileStore, () => {
  defineHeaderTitle();
});

const openArchiveModal = () => {
  archiveStore.person = {
    id: profileStore.person?.id as number,
    display_name: profileStore.person?.display_name as string,
    archived_at: profileStore.person?.archived_at as string,
    people_type: {
      acronym: profileStore.person?.people_type?.acronym as string,
    },
  };
  archiveStore.isShow = true;
};
/**
 * Define the header title text.
 */
const defineHeaderTitle = () => {
  if (isUserList.value) {
    headerTitle.value = headerTitleDefault.value;
  } else {
    showLoader.value = true;
    if (!profileStore.loading && profileStore.person?.display_name) {
      headerTitle.value = profileStore.person?.display_name;
      showLoader.value = false;
    }
  }
};

/**
 * Define a reusable value to check if the current route is the user listing.
 */
const isUserList = computed(
  () =>
    $routing.route.name === 'user.list' ||
    $routing.route.name === 'user.referrals' ||
    $routing.route.name === 'user.to-be-archived',
);
/**
 * Only show the back button if the current view is that of the user listing.
 */
const showBackButton = computed(() => !isUserList.value);

const doToast = (response: AxiosResponse) => {
  const success = response.status === 200;
  toast.add({
    severity: success ? 'success' : 'error',
    summary: success ? 'Unarchived' : 'Error',
    detail: success ? 'User Unarchived.' : response.statusText,
    life: 3000,
  });
};

const updateProfile = () => {
  profileStore.setUser(profileStore.person?.id as number);
};

const unarchivePerson = async () => {
  const response = await archiveStore.unarchive(profileStore.person?.id as number);
  if (response.status === 200) {
    updateProfile();
  }
  doToast(response);
};

const isFromAdminRoute = computed<boolean>(() => $routing.route.name === 'administrators.userProfile');

/**
 * make sure the person editing cannot impersonate or change pwrd
 */
const isNotMe = computed(() => profileStore.id !== authStore.user?.id);

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_AppButton = _resolveComponent("AppButton")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", null, [
        (!isFromAdminRoute.value && showBackButton.value)
          ? (_openBlock(), _createBlock(_component_RouterLink, {
              key: 0,
              to: "/users",
              class: "no-underline"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_Icon, {
                  name: "arrow_back",
                  class: "text-xs"
                }),
                _cache[0] || (_cache[0] = _createElementVNode("div", { class: "link-margin text-xs" }, "All Users", -1))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (isFromAdminRoute.value && showBackButton.value)
          ? (_openBlock(), _createBlock(_component_RouterLink, {
              key: 1,
              to: "/admin/administrators",
              class: "no-underline"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_Icon, {
                  name: "arrow_back",
                  class: "text-xs"
                }),
                _cache[1] || (_cache[1] = _createElementVNode("div", { class: "link-margin text-xs" }, "All Administrators", -1))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (showLoader.value)
          ? (_openBlock(), _createBlock(_component_Skeleton, {
              key: 2,
              width: "20rem",
              height: "2.3rem"
            }))
          : (_openBlock(), _createElementBlock("h1", {
              key: 3,
              textContent: _toDisplayString(headerTitle.value)
            }, null, 8, _hoisted_3))
      ]),
      (!isFromAdminRoute.value)
        ? (_openBlock(), _createBlock(_unref(UserHeaderTotals), { key: 0 }))
        : _createCommentVNode("", true)
    ]),
    (showBackButton.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          (_unref(profileStore).selectedUserRoutes)
            ? (_openBlock(), _createBlock(_unref(AppTabMenu), {
                key: 0,
                items: _unref(profileStore).selectedUserRoutes
              }, null, 8, ["items"]))
            : _createCommentVNode("", true),
          (!_unref(profileStore).loading && isNotMe.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                (_unref(profileStore).person)
                  ? (_openBlock(), _createBlock(_unref(ArchiveModal), {
                      key: 0,
                      onOnArchived: updateProfile
                    }))
                  : _createCommentVNode("", true),
                (_unref(can)([_unref(PERMISSIONS).PEOPLE_ARCHIVE]) && !_unref(profileStore).person?.archived_at)
                  ? (_openBlock(), _createBlock(_component_AppButton, {
                      key: 1,
                      type: "secondary",
                      text: "Archive",
                      "icon-left": "archive",
                      onClick: openArchiveModal
                    }))
                  : _createCommentVNode("", true),
                (_unref(can)([_unref(PERMISSIONS).PEOPLE_UNARCHIVE]) && _unref(profileStore).person?.archived_at)
                  ? (_openBlock(), _createBlock(_component_AppButton, {
                      key: 2,
                      type: "secondary",
                      text: "Unarchive",
                      "icon-left": "unarchive",
                      onClick: unarchivePerson
                    }))
                  : _createCommentVNode("", true),
                (
            _unref(can)([_unref(PERMISSIONS).AUTH_PASSWORD_RESET_USER]) &&
            !_unref(profileStore).person?.archived_at &&
            _unref(profileStore).person?.user
          )
                  ? (_openBlock(), _createBlock(_unref(ResetPasswordButton), { key: 3 }))
                  : _createCommentVNode("", true),
                (_unref(can)([_unref(PERMISSIONS).IMPERSONATE]) && !_unref(profileStore).person?.archived_at)
                  ? (_openBlock(), _createBlock(_unref(ImpersonateButton), { key: 4 }))
                  : _createCommentVNode("", true),
                (_unref(can)([_unref(PERMISSIONS).PEOPLE_SEND_ACTIVATE_LINK]) && !_unref(profileStore).person?.archived_at)
                  ? (_openBlock(), _createBlock(_unref(ActivateUserButton), { key: 5 }))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]))
      : (userRoutes.value.filter((r) => r.visible).length > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createVNode(_unref(AppTabMenu), {
              items: userRoutes.value,
              "active-index": activeIndex.value
            }, null, 8, ["items", "active-index"])
          ]))
        : _createCommentVNode("", true)
  ]))
}
}

})